import classNames from 'classnames'
import moment from 'moment'

import { ManagementOrderDetailsLineItems } from '@apps-common/apps/portal/pages/orders/pages/details/components/management'
import { PortalOrderDetailsLineItems } from '@apps-common/apps/portal/pages/orders/pages/details/components/portal'
import { CardContainer } from '@apps-common/components/common'
import { INPUT_INFO_SM_NO_BORDER_NO_BG, InputInfoSM } from '@apps-common/components/elements'
import { commonIcons } from '@apps-common/config/icons'
import { OrderStatus } from '@apps-common/config/orders'
import { AUTH_CLAIMS, Authorization } from '@apps-common/lib'
import { toastSuccess } from '@apps-common/lib/toast'
import { IOrderDetailsModel } from '@apps-common/types/portal/order'

interface IProps {
  data?: IOrderDetailsModel
  loading: boolean
  updating: boolean
  classes?: string
}

export const OrderDetailsCard = ({ data, loading, updating, classes }: IProps) => {
  const { order, orderLine } = data || {}

  const {
    orderNo,
    instructions,
    bureau,
    monitoringCentre,
    contact,
    email,
    phone,
    streetAddress1,
    streetAddress2,
    streetAddress3,
    streetAddress4,
    postCode,
    city,
    courierName,
    trackingUrl,
    trackingNumber,
    pickUp,
    status,
    orderDate
  } = order || {}

  const CopyIcon = commonIcons.copy
  const ArrowIcon = commonIcons.arrowAngled

  return (
    <CardContainer bgClasses={classNames('bg-surface border border-border', classes)}>
      <div className='grid sm:grid-cols-2 gap-x-[20px]'>
        <InputInfoSM
          id='status'
          label='Status'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={classNames(INPUT_INFO_SM_NO_BORDER_NO_BG, 'jost')}
          loading={loading}
          hideNoEditIcon
          value={
            <div className='flex items-center justify-start gap-[5px]'>
              <p
                className={classNames(
                  'capitalize px-[10px] py-[2px] rounded-full',
                  status === OrderStatus.complete && `bg-success text-on-success`,
                  status === OrderStatus.pending && `bg-info text-on-info`,
                  status === OrderStatus.cancelled && `bg-warning text-on-warning`
                )}
              >
                {status}
              </p>
            </div>
          }
        />
        <InputInfoSM
          id='orderDate'
          label='Date'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER_NO_BG}
          loading={loading}
          hideNoEditIcon
          value={moment(orderDate).format('DD/MM/YYYY')}
        />
        <InputInfoSM
          id='orderNo'
          label='Order#'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER_NO_BG}
          loading={loading}
          hideNoEditIcon
          value={orderNo}
        />
        <InputInfoSM
          id='bureau'
          label='Bureau'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER_NO_BG}
          loading={loading}
          hideNoEditIcon
          value={bureau}
        />
        <InputInfoSM
          id='monitoringCentre'
          label='Monitoring Centre'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER_NO_BG}
          loading={loading}
          hideNoEditIcon
          value={monitoringCentre}
        />
        <InputInfoSM
          id='contact'
          label='Contact'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER_NO_BG}
          loading={loading}
          hideNoEditIcon
          value={
            <div>
              <p>{contact}</p>
              <p>{email}</p>
              <p>{phone}</p>
            </div>
          }
        />
        <InputInfoSM
          id='delivery'
          label='Delivery'
          showLabel
          labelClassName='ml-0 mb-1 rounded-sm jost-medium'
          className={INPUT_INFO_SM_NO_BORDER_NO_BG}
          loading={loading}
          hideNoEditIcon
          value={
            pickUp ? (
              <div>
                <p>To be collected</p>
              </div>
            ) : (
              <div>
                <p>Courier to:</p>
                {streetAddress1 && <p>{streetAddress1}</p>}
                {streetAddress2 && <p>{streetAddress2}</p>}
                {streetAddress3 && <p>{streetAddress3}</p>}
                {streetAddress4 && <p>{streetAddress4}</p>}
                {city && <p>{city}</p>}
                {postCode && <p>{postCode}</p>}
              </div>
            )
          }
        />
        {!pickUp && (
          <InputInfoSM
            id='tracking'
            label='Tracking'
            showLabel
            labelClassName='ml-0 mb-1 rounded-sm jost-medium'
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={
              <div>
                {status !== OrderStatus.complete && <p>Tracking information not available.</p>}
                {status === OrderStatus.complete && trackingNumber && trackingUrl && (
                  <div className='flex items-center justify-start gap-[5px]'>
                    <a href={trackingUrl} target='_blank' rel='noreferrer'>
                      {courierName} {trackingNumber}
                    </a>
                    <button
                      type='button'
                      aria-label='Copy'
                      onClick={() => {
                        toastSuccess(`Copied to clipboard!`)
                        navigator.clipboard.writeText(trackingNumber)
                      }}
                    >
                      <CopyIcon className='relative text-[16px]' />
                    </button>
                    <button
                      type='button'
                      aria-label='Navigate'
                      className='bg-surface-2 border border-border rounded-full w-[18px] h-[18px] flex items-center justify-center'
                      onClick={() => {
                        window.open(trackingUrl, '_blank')
                      }}
                    >
                      <ArrowIcon className='relative text-[12px]' />
                    </button>
                  </div>
                )}
              </div>
            }
          />
        )}
        {instructions && instructions?.length > 0 && (
          <InputInfoSM
            id='instructions'
            label='Instructions'
            showLabel
            labelClassName='ml-0 mb-1 rounded-sm jost-medium'
            className={INPUT_INFO_SM_NO_BORDER_NO_BG}
            loading={loading}
            hideNoEditIcon
            value={instructions}
          />
        )}
      </div>
      <Authorization
        roles={AUTH_CLAIMS.managementAdmin.roles}
        scopes={AUTH_CLAIMS.managementAdmin.scopes}
        notAuthorizedFallback={<PortalOrderDetailsLineItems loading={loading} orderLine={orderLine || []} />}
      >
        <ManagementOrderDetailsLineItems loading={loading} orderLine={orderLine || []} />
      </Authorization>
    </CardContainer>
  )
}
